/**
 * ISPHEleman.ts
 *
 * Base Element interface and factory for SPH simulation system
 * All simulation elements (inlets, air vents, baffles, filters, outlets, sensors, thermal elements, feeders)
 * implement this interface.
 *
 * This establishes the foundation for:
 * - Element type system
 * - GPU buffer serialization
 * - Parameter management for UI binding
 * - Timeline system integration
 * - Element collection management
 */

import type { Vec3, Renk, YerlestirmeModu } from '../FikoSPHParametreler';

/**
 * Enum for all supported element types in the SPH simulation
 */
export enum ElemanTipi {
  DOKUM_GIRISI = 'dokum_girisi',       // Casting inlet (molten metal entry)
  HAVA_DELIGI = 'hava_deligi',         // Air vent (gas escape)
  BESLEYICI = 'besleyici',             // Feeder (pressure head)
  FILTRE = 'filtre',                   // Filter (foam/ceramic)
  ENGEL = 'engel',                     // Baffle/obstacle
  CIKIS = 'cikis',                     // Outlet (overflow/exit)
  SANAL_SENSOR = 'sanal_sensor',       // Virtual sensor (probe)
  TERMAL_ELEMAN = 'termal_eleman',     // Thermal element (heater/cooler)
}

/**
 * Parameter descriptor for dynamic UI generation
 * Used to create property panels and timeline keyframe editors
 */
export interface ElemanParametre {
  ad: string;                          // Parameter internal name
  etiket: string;                      // Display label (Turkish)
  tip: 'number' | 'boolean' | 'string' | 'vec3' | 'renk' | 'enum';
  deger: any;                          // Current value
  varsayilan: any;                     // Default value
  min?: number;                        // For number type
  max?: number;                        // For number type
  adim?: number;                       // Step for slider
  birim?: string;                      // Unit like "m/s", "Pa", "°C"
  enumSecenekler?: { deger: string; etiket: string }[];  // For enum type
  kategori?: string;                   // Group label like "Pozisyon", "Akış", "Timeline"
  aciklama?: string;                   // Tooltip/help text
}

/**
 * Base interface that ALL simulation elements must implement
 *
 * Design principles:
 * - Immutable type and ID (identity)
 * - Serializable to GPU buffers (compute shader input)
 * - Serializable to JSON (save/load)
 * - Self-validating (parameter constraints)
 * - Timeline-aware (activation time ranges)
 * - Position-flexible (world space or mesh-bound)
 */
export interface ISPHEleman {
  // Identity
  readonly id: string;                 // Unique ID (UUID)
  readonly tip: ElemanTipi;            // Element type (immutable)

  // Basic properties
  ad: string;                          // User-given label like "Giriş-1", "Hava-2"
  aktif: boolean;                      // Active on/off (affects simulation)
  gorunur?: boolean;                   // 3D icon visibility (visual only)

  // Spatial properties
  pozisyon: Vec3;                      // World position (meters)
  yon?: Vec3;                          // Direction vector (normalized, used for flow/orientation)
  renk?: Renk | string;               // Display color for 3D icon

  // Placement info
  yerlestirmeModu?: YerlestirmeModu;   // How element is positioned
  bagliMeshId?: string | null;         // If mesh-bound, the mesh ID
  bagliMeshOffset?: Vec3 | null;       // Local offset from mesh origin (if mesh-bound)

  // Timeline
  aktifBaslangic?: number;             // Time when element activates (seconds)
  aktifBitis?: number;                 // Time when element deactivates (Infinity = forever)

  // GPU serialization
  toGPUBytes(): ArrayBuffer;
  gpuStructBoyut(): number;

  // JSON serialization
  toJSON(): any;

  // Validation - returns error messages or validation result
  dogrula(): string[] | { gecerli: boolean; hatalar: string[] };

  // Cloning
  klonla(): any;

  // Parameter system (for UI binding and timeline keyframes)
  parametreListesi(): any[];

  // Set a parameter by name
  parametreAyarla(ad: string, deger: any): void;

  // Get a parameter value by name
  parametreAl(ad: string): any;
}

/**
 * Tracks how many of each type have been created for auto-naming
 * Used by sonrakiElemanAdi() to generate unique names like "Giriş-1", "Giriş-2"
 */
const elemanSayaclari: Map<ElemanTipi, number> = new Map();

/**
 * Generate next auto-name for element type
 * Example: "Giriş-1", "Giriş-2", "Hava-1", "Hava-2"
 */
export function sonrakiElemanAdi(tip: ElemanTipi): string {
  const sayi = (elemanSayaclari.get(tip) || 0) + 1;
  elemanSayaclari.set(tip, sayi);

  const onekler: Record<ElemanTipi, string> = {
    [ElemanTipi.DOKUM_GIRISI]: 'Giriş',
    [ElemanTipi.HAVA_DELIGI]: 'Hava',
    [ElemanTipi.BESLEYICI]: 'Besleyici',
    [ElemanTipi.FILTRE]: 'Filtre',
    [ElemanTipi.ENGEL]: 'Engel',
    [ElemanTipi.CIKIS]: 'Çıkış',
    [ElemanTipi.SANAL_SENSOR]: 'Sensör',
    [ElemanTipi.TERMAL_ELEMAN]: 'Termal',
  };

  return `${onekler[tip]}-${sayi}`;
}

/**
 * Reset all element counters (called when clearing scene)
 */
export function sayaclariSifirla(): void {
  elemanSayaclari.clear();
}

/**
 * Default icon colors for each element type
 * Used when creating new elements (user can override)
 */
export const ELEMAN_VARSAYILAN_RENKLER: Record<ElemanTipi, Renk> = {
  [ElemanTipi.DOKUM_GIRISI]:  { r: 0.2, g: 0.5, b: 1.0, a: 1.0 },  // Blue (molten metal inlet)
  [ElemanTipi.HAVA_DELIGI]:   { r: 0.2, g: 0.9, b: 0.3, a: 1.0 },  // Green (air vent)
  [ElemanTipi.BESLEYICI]:     { r: 1.0, g: 0.6, b: 0.1, a: 1.0 },  // Orange (feeder)
  [ElemanTipi.FILTRE]:        { r: 0.6, g: 0.6, b: 0.6, a: 1.0 },  // Gray (filter)
  [ElemanTipi.ENGEL]:         { r: 1.0, g: 0.2, b: 0.2, a: 1.0 },  // Red (baffle/obstacle)
  [ElemanTipi.CIKIS]:         { r: 0.7, g: 0.2, b: 0.9, a: 1.0 },  // Purple (outlet)
  [ElemanTipi.SANAL_SENSOR]:  { r: 1.0, g: 0.9, b: 0.1, a: 1.0 },  // Yellow (sensor)
  [ElemanTipi.TERMAL_ELEMAN]: { r: 0.9, g: 0.3, b: 0.1, a: 1.0 },  // Red-orange (thermal)
};

/**
 * Max element counts per type (matches GPU buffer sizing in shaders)
 * IMPORTANT: These must match the array sizes in WGSL compute shaders
 */
export const GPU_MAX_ELEMAN_SAYILARI: Record<ElemanTipi, number> = {
  [ElemanTipi.DOKUM_GIRISI]:  64,   // 64 inlets max
  [ElemanTipi.HAVA_DELIGI]:   64,   // 64 air vents max
  [ElemanTipi.BESLEYICI]:     16,   // 16 feeders max
  [ElemanTipi.FILTRE]:        32,   // 32 filters max
  [ElemanTipi.ENGEL]:         32,   // 32 baffles max
  [ElemanTipi.CIKIS]:         32,   // 32 outlets max
  [ElemanTipi.SANAL_SENSOR]:  64,   // 64 sensors max
  [ElemanTipi.TERMAL_ELEMAN]: 32,   // 32 thermal elements max
};

/**
 * Element Collection Manager
 *
 * Centralized management of all simulation elements:
 * - Add/remove/query elements
 * - Filter by type/active status
 * - Generate GPU buffers for all elements of a type
 * - Serialize/deserialize entire scene
 *
 * Used by:
 * - FikoSPHSivi main simulation class
 * - Element editor UI
 * - GPU compute pipeline
 * - Save/load system
 */
export class ElemanYonetici {
  private elemanlar: Map<string, ISPHEleman> = new Map();

  /**
   * Add element to collection
   */
  ekle(eleman: ISPHEleman): void {
    this.elemanlar.set(eleman.id, eleman);
  }

  /**
   * Remove element by ID
   * Returns true if element was found and removed
   */
  sil(id: string): boolean {
    return this.elemanlar.delete(id);
  }

  /**
   * Get element by ID
   * Returns undefined if not found
   */
  al(id: string): ISPHEleman | undefined {
    return this.elemanlar.get(id);
  }

  /**
   * Get all elements of a specific type
   */
  tipGore(tip: ElemanTipi): ISPHEleman[] {
    return Array.from(this.elemanlar.values()).filter(e => e.tip === tip);
  }

  /**
   * Get all active elements, optionally filtered by type
   * Active means: aktif=true AND current time within [aktifBaslangic, aktifBitis]
   */
  aktifler(tip?: ElemanTipi): ISPHEleman[] {
    let sonuc = Array.from(this.elemanlar.values()).filter(e => e.aktif);
    if (tip !== undefined) {
      sonuc = sonuc.filter(e => e.tip === tip);
    }
    return sonuc;
  }

  /**
   * Get all elements (unsorted)
   */
  hepsi(): ISPHEleman[] {
    return Array.from(this.elemanlar.values());
  }

  /**
   * Get element counts by type
   * Returns object like { dokum_girisi: 3, hava_deligi: 2, ... }
   */
  sayilar(): Record<ElemanTipi, number> {
    const sonuc = {} as Record<ElemanTipi, number>;
    for (const tip of Object.values(ElemanTipi)) {
      sonuc[tip] = this.tipGore(tip).length;
    }
    return sonuc;
  }

  /**
   * Write all elements of a specific type to a GPU buffer (ArrayBuffer)
   * Returns the packed ArrayBuffer ready for queue.writeBuffer()
   *
   * Buffer layout:
   * - Fixed size based on GPU_MAX_ELEMAN_SAYILARI[tip]
   * - Each element occupies gpuStructBoyut() bytes
   * - Unused slots are zero-initialized
   *
   * Example:
   *   const buffer = yonetici.tipGPUBufferOlustur(ElemanTipi.DOKUM_GIRISI);
   *   queue.writeBuffer(girislerBuffer, 0, buffer);
   */
  tipGPUBufferOlustur(tip: ElemanTipi): ArrayBuffer {
    const elemanlar = this.tipGore(tip);

    // Early return for empty case
    if (elemanlar.length === 0) {
      const maxSayi = GPU_MAX_ELEMAN_SAYILARI[tip];
      // Need to determine struct size - use a dummy element or fixed size
      // For now, return empty buffer (caller should handle this)
      return new ArrayBuffer(0);
    }

    const boyut = elemanlar[0].gpuStructBoyut();
    const maxSayi = GPU_MAX_ELEMAN_SAYILARI[tip];
    const buffer = new ArrayBuffer(boyut * maxSayi);

    // Pack each element into buffer
    for (let i = 0; i < Math.min(elemanlar.length, maxSayi); i++) {
      const bytes = elemanlar[i].toGPUBytes();
      const view = new Uint8Array(buffer, i * boyut, boyut);
      view.set(new Uint8Array(bytes));
    }

    // Remaining slots are already zero-initialized (ArrayBuffer default)
    return buffer;
  }

  /**
   * Serialize all elements to JSON
   * Format: [{ tip: 'dokum_girisi', data: {...} }, ...]
   * Used for scene save/load
   */
  toJSON(): any {
    return Array.from(this.elemanlar.values()).map(e => ({
      tip: e.tip,
      data: e.toJSON(),
    }));
  }

  /**
   * Clear all elements and reset counters
   * Used when loading new scene or resetting simulation
   */
  temizle(): void {
    this.elemanlar.clear();
    sayaclariSifirla();
  }

  /**
   * Get total element count
   */
  toplam(): number {
    return this.elemanlar.size;
  }

  /**
   * Validate all elements
   * Returns array of { id, ad, hatalar[] } for each element with errors
   */
  tumunuDogrula(): { id: string; ad: string; hatalar: string[] }[] {
    const sonuc: { id: string; ad: string; hatalar: string[] }[] = [];

    for (const eleman of this.elemanlar.values()) {
      const sonucDogrula = eleman.dogrula();
      const hatalar = Array.isArray(sonucDogrula) ? sonucDogrula : sonucDogrula.hatalar;
      if (hatalar.length > 0) {
        sonuc.push({
          id: eleman.id,
          ad: eleman.ad,
          hatalar,
        });
      }
    }

    return sonuc;
  }

  /**
   * Check if element count exceeds GPU limits
   * Returns array of { tip, sayi, limit } for types that exceed limits
   */
  limitleriKontrolEt(): { tip: ElemanTipi; sayi: number; limit: number }[] {
    const sonuc: { tip: ElemanTipi; sayi: number; limit: number }[] = [];

    for (const tip of Object.values(ElemanTipi)) {
      const sayi = this.tipGore(tip).length;
      const limit = GPU_MAX_ELEMAN_SAYILARI[tip];
      if (sayi > limit) {
        sonuc.push({ tip, sayi, limit });
      }
    }

    return sonuc;
  }
}

/**
 * Simple UUID v4 generator (no external dependencies)
 * Used for element ID generation
 *
 * Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * Where x is random hex digit, y is one of [8,9,a,b]
 */
export function olusturId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Helper to create Vec3 with validation
 */
export function vec3Olustur(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

/**
 * Helper to create Renk with validation (clamps to 0-1)
 */
export function renkOlustur(r: number, g: number, b: number, a: number = 1.0): Renk {
  return {
    r: Math.max(0, Math.min(1, r)),
    g: Math.max(0, Math.min(1, g)),
    b: Math.max(0, Math.min(1, b)),
    a: Math.max(0, Math.min(1, a)),
  };
}

/**
 * Helper to normalize a direction vector
 */
export function yonNormalize(yon: Vec3): Vec3 {
  const len = Math.sqrt(yon.x * yon.x + yon.y * yon.y + yon.z * yon.z);
  if (len < 0.0001) {
    // Degenerate case - return default up vector
    return { x: 0, y: 1, z: 0 };
  }
  return {
    x: yon.x / len,
    y: yon.y / len,
    z: yon.z / len,
  };
}

/**
 * Helper to copy Vec3
 */
export function vec3Kopyala(v: Vec3): Vec3 {
  return { x: v.x, y: v.y, z: v.z };
}

/**
 * Helper to copy Renk
 */
export function renkKopyala(r: Renk): Renk {
  return { r: r.r, g: r.g, b: r.b, a: r.a };
}

/**
 * Helper to check if two Vec3 are equal (with epsilon)
 */
export function vec3Esit(a: Vec3, b: Vec3, epsilon: number = 0.0001): boolean {
  return (
    Math.abs(a.x - b.x) < epsilon &&
    Math.abs(a.y - b.y) < epsilon &&
    Math.abs(a.z - b.z) < epsilon
  );
}

/**
 * Helper to lerp between two Vec3
 */
export function vec3Lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

/**
 * Helper to lerp between two Renk
 */
export function renkLerp(a: Renk, b: Renk, t: number): Renk {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
    a: a.a + (b.a - a.a) * t,
  };
}
